import React, { useState } from 'react';
import {
    IoCheckmarkCircle,
    IoWarning,
    IoCloseCircle,
    IoEye,
    IoSearch,
    IoGlobe,
    IoImage,
    IoText,
    IoLink,
    IoStar,
    IoCodeSlash,
} from 'react-icons/io5';
import styles from '../AddProduct.module.css';

function SEOAudit({
    productName,
    metaTitle,
    metaDescription,
    slug,
    focusKeywords,
    brandName,
    availability,
    productCondition,
    images,
    averageRating,
    totalReviews,
    faqs,
    breadcrumbs,
    HtmlDesc,
    sellingPrice,
    category,
    errorFields,
}) {
    const [showDetails, setShowDetails] = useState(false);

    const performSEOAudit = () => {
        const audit = {
            score: 0,
            total: 0,
            checks: [],
        };

        // Basic Information Checks
        const basicChecks = [
            {
                name: 'Product Name',
                status:
                    productName &&
                    productName.length >= 10 &&
                    productName.length <= 60,
                weight: 10,
                message: productName
                    ? productName.length < 10
                        ? 'Product name too short (min 10 chars)'
                        : productName.length > 60
                        ? 'Product name too long (max 60 chars)'
                        : 'Good length'
                    : 'Product name is required',
                icon: IoText,
                critical: true,
            },
            {
                name: 'Meta Title',
                status:
                    metaTitle &&
                    metaTitle.length >= 10 &&
                    metaTitle.length <= 60,
                weight: 15,
                message: metaTitle
                    ? metaTitle.length < 10
                        ? 'Meta title too short (min 10 chars)'
                        : metaTitle.length > 60
                        ? 'Meta title too long (max 60 chars)'
                        : metaTitle.length < 30
                        ? 'Good, but 30-60 chars is ideal'
                        : 'Perfect length'
                    : 'Meta title is required',
                icon: IoText,
                critical: !metaTitle || metaTitle.length < 10,
            },
            {
                name: 'Meta Description',
                status:
                    metaDescription &&
                    metaDescription.length >= 50 &&
                    metaDescription.length <= 160,
                weight: 12,
                message: metaDescription
                    ? metaDescription.length < 50
                        ? 'Meta description too short (min 50 chars)'
                        : metaDescription.length > 160
                        ? 'Meta description too long (max 160 chars)'
                        : metaDescription.length < 120
                        ? 'Good, but 120-160 chars is ideal'
                        : 'Perfect length'
                    : 'Meta description is required',
                icon: IoText,
                critical: !metaDescription || metaDescription.length < 50,
            },
            {
                name: 'URL Slug',
                status: slug && slug.length > 0 && /^[a-z0-9-]+$/.test(slug),
                weight: 8,
                message: slug
                    ? /^[a-z0-9-]+$/.test(slug)
                        ? 'SEO-friendly URL'
                        : 'URL should contain only lowercase letters, numbers, and hyphens'
                    : 'URL slug is required',
                icon: IoLink,
                critical: true,
            },
            {
                name: 'Focus Keywords',
                status: focusKeywords && focusKeywords.length > 0,
                weight: 10,
                message:
                    focusKeywords && focusKeywords.length > 0
                        ? `${focusKeywords.length} keyword${
                              focusKeywords.length !== 1 ? 's' : ''
                          } added`
                        : 'Add focus keywords for better SEO',
                icon: IoSearch,
                critical: false,
            },
            {
                name: 'Brand Name',
                status: brandName && brandName.length > 0,
                weight: 5,
                message: brandName
                    ? 'Brand name provided'
                    : 'Brand name recommended',
                icon: IoGlobe,
                critical: false,
            },
        ];

        // Content Quality Checks
        const contentChecks = [
            {
                name: 'Product Description',
                status: HtmlDesc && HtmlDesc.length >= 200,
                weight: 15,
                message: HtmlDesc
                    ? HtmlDesc.length < 200
                        ? 'Description too short (min 200 chars)'
                        : 'Good description length'
                    : 'Product description is required',
                icon: IoText,
                critical: true,
            },
            {
                name: 'Product Images',
                status: images && images.length > 0,
                weight: 10,
                message:
                    images && images.length > 0
                        ? `${images.length} image${
                              images.length !== 1 ? 's' : ''
                          } uploaded`
                        : 'At least one product image is required',
                icon: IoImage,
                critical: true,
            },
            {
                name: 'Image Alt Text',
                status:
                    images &&
                    images.length > 0 &&
                    images.every(
                        (img) => img.altText && img.altText.length > 0
                    ),
                weight: 8,
                message:
                    images && images.length > 0
                        ? images.every(
                              (img) => img.altText && img.altText.length > 0
                          )
                            ? 'All images have alt text'
                            : 'Some images missing alt text'
                        : 'No images to check',
                icon: IoImage,
                critical: false,
            },
            {
                name: 'Product Price',
                status: sellingPrice && sellingPrice > 0,
                weight: 5,
                message:
                    sellingPrice && sellingPrice > 0
                        ? `Price: ৳${sellingPrice}`
                        : 'Product price is required',
                icon: IoText,
                critical: true,
            },
            {
                name: 'Category Selection',
                status: category && category.length > 0,
                weight: 5,
                message:
                    category && category.length > 0
                        ? 'Category selected'
                        : 'Product category is required',
                icon: IoGlobe,
                critical: true,
            },
        ];

        // Advanced SEO Checks
        const advancedChecks = [
            {
                name: 'Product Reviews',
                status: totalReviews > 0,
                weight: 8,
                message:
                    totalReviews > 0
                        ? `${totalReviews} review${
                              totalReviews !== 1 ? 's' : ''
                          } (${averageRating.toFixed(1)}/5 stars)`
                        : 'No reviews yet (recommended for SEO)',
                icon: IoStar,
                critical: false,
            },
            {
                name: 'FAQ Section',
                status: faqs && faqs.length > 0,
                weight: 6,
                message:
                    faqs && faqs.length > 0
                        ? `${faqs.length} FAQ${
                              faqs.length !== 1 ? 's' : ''
                          } added`
                        : 'FAQ section recommended for SEO',
                icon: IoText,
                critical: false,
            },
            {
                name: 'Breadcrumb Navigation',
                status: breadcrumbs && breadcrumbs.length > 0,
                weight: 4,
                message:
                    breadcrumbs && breadcrumbs.length > 0
                        ? `${breadcrumbs.length} breadcrumb${
                              breadcrumbs.length !== 1 ? 's' : ''
                          } configured`
                        : 'Breadcrumb navigation recommended',
                icon: IoLink,
                critical: false,
            },
            {
                name: 'Schema Markup',
                status:
                    productName &&
                    sellingPrice &&
                    images &&
                    images.length > 0 &&
                    brandName,
                weight: 10,
                message:
                    productName &&
                    sellingPrice &&
                    images &&
                    images.length > 0 &&
                    brandName
                        ? 'Schema markup ready'
                        : 'Missing required fields for schema markup',
                icon: IoCodeSlash,
                critical: false,
            },
        ];

        // Combine all checks
        const allChecks = [...basicChecks, ...contentChecks, ...advancedChecks];

        allChecks.forEach((check) => {
            audit.total += check.weight;
            if (check.status) {
                audit.score += check.weight;
            }
            audit.checks.push({
                ...check,
                score: check.status ? check.weight : 0,
            });
        });

        return audit;
    };

    const audit = performSEOAudit();
    const scorePercentage = Math.round((audit.score / audit.total) * 100);

    const getScoreColor = (score) => {
        if (score >= 90) return '#27ae60';
        if (score >= 70) return '#f39c12';
        return '#e74c3c';
    };

    const getScoreText = (score) => {
        if (score >= 90) return 'Excellent';
        if (score >= 70) return 'Good';
        if (score >= 50) return 'Needs Improvement';
        return 'Poor';
    };

    const criticalIssues = audit.checks.filter(
        (check) => check.critical && !check.status
    );
    const recommendations = audit.checks.filter(
        (check) => !check.critical && !check.status
    );

    return (
        <div className={`${styles['AddProduct-card']} ${styles['AddProduct-card-full-width']}`}>
            <div className={styles['AddProduct-card-header']}>
                <div className={styles['AddProduct-card-title']}>
                    <IoSearch className={styles['AddProduct-card-icon']} />
                    <span>SEO Audit</span>
                </div>
                <div className={styles['AddProduct-card-subtitle']}>
                    Comprehensive SEO analysis and recommendations
                </div>
            </div>

            <div className={styles['seo-audit-container']}>
                {/* SEO Score Overview */}
                <div className={styles['seo-score-section']}>
                    <div className={styles['seo-score-header']}>
                        <h4>SEO Score</h4>
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className={styles['seo-details-button']}
                        >
                            <IoEye />
                            {showDetails ? 'Hide' : 'Show'} Details
                        </button>
                    </div>

                    <div className={styles['seo-score-display']}>
                        <div className={styles['seo-score-circle']}>
                            <div
                                className={styles['seo-score-progress']}
                                style={{
                                    background: `conic-gradient(${getScoreColor(
                                        scorePercentage
                                    )} 0deg ${
                                        scorePercentage * 3.6
                                    }deg, #e9ecef ${
                                        scorePercentage * 3.6
                                    }deg 360deg)`,
                                }}
                            >
                                <div className={styles['seo-score-inner']}>
                                    <span
                                        className={styles['seo-score-number']}
                                    >
                                        {scorePercentage}
                                    </span>
                                    <span
                                        className={styles['seo-score-percent']}
                                    >
                                        %
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles['seo-score-info']}>
                            <div className={styles['seo-score-text']}>
                                {getScoreText(scorePercentage)}
                            </div>
                            <div className={styles['seo-score-details']}>
                                {audit.score} / {audit.total} points
                            </div>
                        </div>
                    </div>
                </div>

                {/* Issues and Recommendations - Single Column */}
                <div className={styles['seo-analysis-section']}>
                    {/* Critical Issues */}
                    {criticalIssues.length > 0 && (
                        <div className={styles['seo-issues-section']}>
                            <div className={styles['seo-issues-header']}>
                                <IoCloseCircle
                                    className={styles['seo-issues-icon']}
                                />
                                <h4>
                                    Critical Issues ({criticalIssues.length})
                                </h4>
                            </div>
                            <div className={styles['seo-issues-list']}>
                                {criticalIssues.map((issue, index) => (
                                    <div
                                        key={index}
                                        className={styles['seo-issue-item']}
                                    >
                                        <issue.icon
                                            className={styles['seo-issue-icon']}
                                        />
                                        <div
                                            className={
                                                styles['seo-issue-content']
                                            }
                                        >
                                            <div
                                                className={
                                                    styles['seo-issue-name']
                                                }
                                            >
                                                {issue.name}
                                            </div>
                                            <div
                                                className={
                                                    styles['seo-issue-message']
                                                }
                                            >
                                                {issue.message}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Recommendations */}
                    {recommendations.length > 0 && (
                        <div className={styles['seo-recommendations-section']}>
                            <div
                                className={styles['seo-recommendations-header']}
                            >
                                <IoWarning
                                    className={
                                        styles['seo-recommendations-icon']
                                    }
                                />
                                <h4>
                                    Recommendations ({recommendations.length})
                                </h4>
                            </div>
                            <div className={styles['seo-recommendations-list']}>
                                {recommendations.map((rec, index) => (
                                    <div
                                        key={index}
                                        className={
                                            styles['seo-recommendation-item']
                                        }
                                    >
                                        <rec.icon
                                            className={
                                                styles[
                                                    'seo-recommendation-icon'
                                                ]
                                            }
                                        />
                                        <div
                                            className={
                                                styles[
                                                    'seo-recommendation-content'
                                                ]
                                            }
                                        >
                                            <div
                                                className={
                                                    styles[
                                                        'seo-recommendation-name'
                                                    ]
                                                }
                                            >
                                                {rec.name}
                                            </div>
                                            <div
                                                className={
                                                    styles[
                                                        'seo-recommendation-message'
                                                    ]
                                                }
                                            >
                                                {rec.message}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Detailed Analysis */}
                {showDetails && (
                    <div className={styles['seo-details-section']}>
                        <div className={styles['seo-details-header']}>
                            <h4>Detailed Analysis</h4>
                            <p>Complete breakdown of all SEO factors</p>
                        </div>

                        <div className={styles['seo-checks-grid']}>
                            {audit.checks.map((check, index) => (
                                <div
                                    key={index}
                                    className={styles['seo-check-item']}
                                >
                                    <div className={styles['seo-check-header']}>
                                        <check.icon
                                            className={styles['seo-check-icon']}
                                        />
                                        <span
                                            className={styles['seo-check-name']}
                                        >
                                            {check.name}
                                        </span>
                                        <div
                                            className={
                                                styles['seo-check-status']
                                            }
                                        >
                                            {check.status ? (
                                                <IoCheckmarkCircle
                                                    className={
                                                        styles['seo-check-pass']
                                                    }
                                                />
                                            ) : (
                                                <IoCloseCircle
                                                    className={
                                                        styles['seo-check-fail']
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className={styles['seo-check-message']}
                                    >
                                        {check.message}
                                    </div>
                                    <div className={styles['seo-check-score']}>
                                        {check.score} / {check.weight} points
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* SEO Tips */}
                <div className={styles['seo-tips-section']}>
                    <h4>SEO Best Practices</h4>
                    <div className={styles['seo-tips-grid']}>
                        <div className={styles['seo-tip']}>
                            <IoText className={styles['seo-tip-icon']} />
                            <div className={styles['seo-tip-content']}>
                                <h5>Content Quality</h5>
                                <p>
                                    Write unique, valuable content with proper
                                    heading structure
                                </p>
                            </div>
                        </div>
                        <div className={styles['seo-tip']}>
                            <IoImage className={styles['seo-tip-icon']} />
                            <div className={styles['seo-tip-content']}>
                                <h5>Image Optimization</h5>
                                <p>
                                    Use descriptive alt text and optimize image
                                    file sizes
                                </p>
                            </div>
                        </div>
                        <div className={styles['seo-tip']}>
                            <IoStar className={styles['seo-tip-icon']} />
                            <div className={styles['seo-tip-content']}>
                                <h5>User Signals</h5>
                                <p>
                                    Encourage reviews and ratings for better
                                    search rankings
                                </p>
                            </div>
                        </div>
                        <div className={styles['seo-tip']}>
                            <IoCodeSlash className={styles['seo-tip-icon']} />
                            <div className={styles['seo-tip-content']}>
                                <h5>Technical SEO</h5>
                                <p>
                                    Implement schema markup and structured data
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SEOAudit;
